import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const tools = [
  {
    name: 'Chat Tradicional',
    description: 'Converse com IA igual ao ChatGPT para qualquer dúvida ou tarefa.',
    route: '/ai-text-generator',
    icon: '💬'
  },
  {
    name: 'Portal do Estudante',
    description: 'Ideal para qualquer área ou assunto acadêmico.',
    route: '/student-chat',
    icon: '🎓'
  },
  {
    name: 'Assistente do Professor(a)',
    description: 'Ferramentas inteligentes para educadores.',
    route: '/teacher-chat',
    icon: '📚'
  },
  {
    name: 'Ferramenta do(a) Funcionário(a)',
    description: 'Assistente profissional para o dia a dia no trabalho.',
    route: '/employee-tool',
    icon: '💼'
  },
  {
    name: 'Ferramenta do(a) Empreendedor(a)',
    description: 'Soluções inteligentes para quem empreende.',
    route: '/entrepreneur-tool',
    icon: '🚀'
  },
  {
    name: 'Melhore sua Saúde',
    description: 'Orientações para saúde física, mental e emocional.',
    route: '/health-improvement',
    icon: '❤️'
  },
  {
    name: 'Coach de Idiomas',
    description: 'Aprenda e pratique novos idiomas com IA.',
    route: '/language-coach',
    icon: '🌍'
  },
  {
    name: 'Solução de Ouro',
    description: 'Encontre a melhor solução para qualquer problema.',
    route: '/golden-solution',
    icon: '⭐'
  }
];

const valueCards = [
  {
    icon: '🧠',
    title: 'Resolva qualquer problema',
    description: 'Use IA + conhecimento humano para encontrar soluções claras e rápidas.'
  },
  {
    icon: '🎓',
    title: 'Aprenda qualquer assunto',
    description: 'Estude qualquer área com assistência inteligente personalizada.'
  },
  {
    icon: '🚀',
    title: 'Produza mais no trabalho',
    description: 'Ferramentas profissionais para estudar, trabalhar e empreender.'
  }
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [showTrialPopup, setShowTrialPopup] = useState(false);

  const handleAssinarPro = async () => {
    // 1️⃣ Aguardar autenticação carregar antes de verificar
    if (authLoading) {
      return;
    }

    // 2️⃣ Verificar autenticação — redirecionar para /login se não autenticado
    if (!user) {
      navigate('/login');
      return;
    }

    setCheckoutError(null);
    setCheckoutLoading(true);

    try {
      // 3️⃣ Chamar Edge Function
      const country = Intl.DateTimeFormat()?.resolvedOptions()?.locale?.split('-')?.[1];
      const { data, error } = await supabase?.functions?.invoke('create-checkout', {
        body: {
          user_id: user?.id,
          email: user?.email,
          country: country
        }
      });

      console.log('Resposta checkout:', data);
      console.log('Erro checkout:', error);

      // 4️⃣ Verificar erro da função
      if (error) {
        throw new Error(error.message || 'Erro ao conectar com o servidor.');
      }

      // 5️⃣ Redirecionar para Stripe se URL recebida
      const url = data?.url || data?.checkout_url;
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('URL de pagamento não recebida. Tente novamente.');
      }
    } catch (err) {
      console.error('Erro ao iniciar checkout:', err);
      setCheckoutError(
        err?.message || 'Ocorreu um erro ao iniciar o pagamento. Tente novamente.'
      );
    } finally {
      // 6️⃣ Garantir que loading sempre para
      setCheckoutLoading(false);
    }
  };

  const handleTrialClick = () => {
    navigate('/register');
  };

  const handleToolClick = (route) => {
    navigate('/login');
  };

  const handlePremiumClick = () => {
    navigate('/login');
  };

  return (
    <>
      <Helmet>
        <title>Dashboard - AI7X7</title>
        <meta name="description" content="Central de ferramentas AI7X7 - Sua solução com IA e Humanos Reais." />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>
      <div className="min-h-screen" style={{ backgroundColor: '#F7F7F8', fontFamily: "'DM Sans', sans-serif" }}>

        {/* ── TOP BAR ── */}
        <header className="w-full border-b border-gray-200/80 bg-white/90 backdrop-blur-md sticky top-0 z-40">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 h-14 sm:h-16 flex items-center justify-between">
            <span className="text-base sm:text-lg font-bold text-gray-900 tracking-tight select-none">AI7X7</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center text-gray-700 text-xs sm:text-sm font-semibold px-4 sm:px-5 py-2 sm:py-2.5 rounded-full border border-gray-300 hover:bg-gray-50 transition-all duration-200 tracking-wide"
              >
                Entrar
              </button>
              <button
                onClick={() => navigate('/register')}
                className="inline-flex items-center text-white text-xs sm:text-sm font-semibold px-4 sm:px-5 py-2 sm:py-2.5 rounded-full shadow-sm transition-all duration-200 tracking-wide"
                style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}
              >
                Criar Conta
              </button>
            </div>
          </div>
        </header>

        {/* ── MAIN CONTENT ── */}
        <main className="w-full">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">

            {/* ── HERO SECTION ── */}
            <section className="text-center pt-14 pb-10 sm:pt-20 sm:pb-14 lg:pt-24 lg:pb-16">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 tracking-tight mb-4 sm:mb-5 leading-tight">
                Bem-vindo ao AI7X7
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-gray-500 font-normal mb-8 sm:mb-10 max-w-lg mx-auto leading-relaxed">
                Sua central de Solução com IA e com Humanos Reais
              </p>
              <button
                onClick={handleTrialClick}
                className="inline-flex items-center justify-center text-white text-sm sm:text-base font-semibold px-7 sm:px-9 py-3 sm:py-3.5 rounded-full shadow-md hover:shadow-lg transition-all duration-200 tracking-wide hover:scale-[1.02] active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)' }}
              >
                TESTE GRATUITAMENTE
              </button>
            </section>

            {/* ── VALUE CARDS ── */}
            <section className="pb-10 sm:pb-14 lg:pb-16">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
                {valueCards?.map((card, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl border border-gray-200/80 p-6 sm:p-7 text-center hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)' }}
                  >
                    <div className="text-3xl sm:text-4xl mb-4">{card?.icon}</div>
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 leading-snug">
                      {card?.title}
                    </h3>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      {card?.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* ── SECTION LABEL ── */}
            <div className="flex items-center gap-3 mb-6 sm:mb-8">
              <div className="h-px flex-1 bg-gray-200"></div>
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest px-2">FERRAMENTAS DE IA DISPONÍVEIS</span>
              <div className="h-px flex-1 bg-gray-200"></div>
            </div>

            {/* ── TOOLS GRID ── */}
            <section className="pb-10 sm:pb-14">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {tools?.map((tool, index) => (
                  <div
                    key={index}
                    onClick={() => handleToolClick(tool?.route)}
                    className="group bg-white rounded-2xl border border-gray-200/80 p-5 sm:p-6 cursor-pointer hover:shadow-md hover:border-gray-300 hover:-translate-y-0.5 transition-all duration-200 flex flex-col"
                    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)' }}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <span className="text-2xl flex-shrink-0 mt-0.5">{tool?.icon}</span>
                      <h3 className="text-sm font-semibold text-gray-900 group-hover:text-gray-700 transition-colors duration-200 leading-snug">
                        {tool?.name}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-500 leading-relaxed flex-1 mb-5">
                      {tool?.description}
                    </p>
                    <button
                      onClick={(e) => { e?.stopPropagation(); handleToolClick(tool?.route); }}
                      className="w-full text-sm font-semibold text-gray-700 bg-gray-50 hover:bg-gray-900 hover:text-white border border-gray-200 hover:border-gray-900 rounded-xl py-2.5 transition-all duration-200"
                    >
                      Abrir
                    </button>
                  </div>
                ))}
              </div>
            </section>

            {/* ── PREMIUM SUPPORT SECTION ── */}
            <section className="mb-12 sm:mb-16">

              {/* Intro highlight text */}
              <div className="mb-8 sm:mb-10 text-center">
                <p className="inline-block text-sm sm:text-base font-semibold px-5 py-2.5 rounded-full" style={{ backgroundColor: '#e6f7f2', color: '#10a37f' }}>
                  Além de ganhar acesso às nossas ferramentas de IA, você também terá acesso ao Atendimento Premium.
                </p>
              </div>

              {/* Premium Card — HIGH CONVERSION */}
              <div
                className="rounded-2xl p-7 sm:p-10 mb-10 sm:mb-12 text-center"
                style={{
                  background: 'linear-gradient(135deg, #0d8f6f 0%, #10a37f 60%, #12b88f 100%)',
                  boxShadow: '0 8px 32px rgba(16,163,127,0.25), 0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-semibold px-3.5 py-1.5 rounded-full mb-5">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                  Suporte Humano Real
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 tracking-tight">
                  Atendimento Premium
                </h2>
                <p className="text-white/90 text-base sm:text-lg mb-8 max-w-xl mx-auto leading-relaxed">
                  Fale com um especialista humano, receba orientação personalizada e evolua mais rápido com suporte real.
                </p>
                <button
                  onClick={handlePremiumClick}
                  className="inline-flex items-center justify-center gap-2 bg-white font-bold text-sm sm:text-base px-8 sm:px-10 py-3.5 sm:py-4 rounded-full shadow-lg hover:shadow-xl hover:scale-[1.03] active:scale-[0.98] transition-all duration-200"
                  style={{ color: '#10a37f' }}
                >
                  <span>Quero falar com um especialista</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </button>
                {!user && (
                  <p className="text-white/70 text-xs mt-3">Você será redirecionado para o login</p>
                )}
              </div>

              {/* What is Premium Support */}
              <div className="mb-10 sm:mb-12">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
                  O que é o Atendimento Premium?
                </h2>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-3xl">
                  O Atendimento Premium é um suporte humano real oferecido pelo AI7X7, onde você recebe orientação personalizada, acompanhamento contínuo e ajuda prática para evoluir em qualquer área ou desafio. Aqui, você pode tirar dúvidas, encontrar soluções, se desenvolver profissionalmente e até se tornar especialista em um tema específico, com apoio direto de um ser humano preparado para te guiar com clareza, estratégia e motivação.
                </p>
              </div>

              {/* 4 Content Sections Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">

                {/* Section 1 — O que você tem direito */}
                <div
                  className="bg-white rounded-2xl border border-gray-200/80 p-6 sm:p-8"
                  style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)' }}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0" style={{ backgroundColor: '#e6f7f2' }}>
                      ✅
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">O que você tem direito</h3>
                  </div>
                  <ul className="space-y-3">
                    {[
                      'Atendimento humano real',
                      'Solução personalizada sobre qualquer assunto',
                      'Escolha um tema para se tornar especialista com acompanhamento de 1 mês até 1 ano',
                      'Motivação contínua para manter foco e evolução',
                      'Respostas diretas e práticas',
                      'Apoio para encontrar soluções'
                    ]?.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: '#e6f7f2' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="#10a37f" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                        <span className="text-sm sm:text-base text-gray-700 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Section 2 — Para quem é */}
                <div
                  className="bg-white rounded-2xl border border-gray-200/80 p-6 sm:p-8"
                  style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)' }}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0" style={{ backgroundColor: '#e6f7f2' }}>
                      ⚡
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">Para quem é</h3>
                  </div>
                  <ul className="space-y-3">
                    {[
                      'Estudantes, professores e profissionais de qualquer área',
                      'Pessoas que querem resultados mais rápidos',
                      'Pessoas que querem algo além da IA',
                      'Pessoas que buscam orientação personalizada'
                    ]?.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: '#e6f7f2' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="#10a37f" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                        <span className="text-sm sm:text-base text-gray-700 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Section 3 — Por que confiar */}
                <div
                  className="bg-white rounded-2xl border border-gray-200/80 p-6 sm:p-8"
                  style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)' }}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0" style={{ backgroundColor: '#e6f7f2' }}>
                      🔒
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">Por que confiar</h3>
                  </div>
                  <ul className="space-y-3">
                    {[
                      'Atendimento direto',
                      'Privacidade garantida',
                      'Respostas reais'
                    ]?.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: '#e6f7f2' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="#10a37f" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                        <span className="text-sm sm:text-base text-gray-700 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Section 4 — Regras */}
                <div
                  className="bg-white rounded-2xl border border-gray-200/80 p-6 sm:p-8"
                  style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)' }}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0" style={{ backgroundColor: '#e6f7f2' }}>
                      📜
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">Regras</h3>
                  </div>
                  <ul className="space-y-3">
                    {[
                      'Pode escolher qualquer tema dentro da sua área',
                      'Não realizamos diagnósticos',
                      'Não sugerimos tratamentos',
                      'Não interpretamos exames',
                      'Não damos opinião sobre pacientes reais',
                      'Atuamos dentro da lei',
                      'Respeito total (sem preconceito)',
                      'Foco em evolução profissional e pessoal'
                    ]?.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: '#fff3e0' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="#f59e0b" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                        <span className="text-sm sm:text-base text-gray-700 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

              </div>
            </section>

          </div>
        </main>

        {/* ── CHECKOUT ERROR TOAST ── */}
        {checkoutError && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4">
            <div className="bg-white border border-red-200 text-red-600 text-sm font-medium px-5 py-3 rounded-xl shadow-lg flex items-center gap-3">
              <span className="flex-1">{checkoutError}</span>
              <button onClick={() => setCheckoutError(null)} className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0">
                ✕
              </button>
            </div>
          </div>
        )}

        {/* ── TRIAL ENDED POPUP ── */}
        {showTrialPopup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 sm:p-10 max-w-sm w-full text-center">
              <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-5 text-2xl">
                🔒
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Seu plano gratuito acabou.</h2>
              <p className="text-sm text-gray-500 mb-7 leading-relaxed">
                Assine o plano PRO para continuar usando todas as ferramentas sem limites.
              </p>
              <button
                onClick={handleAssinarPro}
                disabled={checkoutLoading}
                className="w-full inline-flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)' }}
              >
                {checkoutLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span>Processando...</span>
                  </>
                ) : (
                  <span>ASSINAR PRO</span>
                )}
              </button>
              {checkoutError && (
                <p className="mt-3 text-xs text-red-600">{checkoutError}</p>
              )}
              <button
                onClick={() => setShowTrialPopup(false)}
                className="mt-4 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default Dashboard;